<template>
  <div class="metrics-action-plan">
    <template v-if="!isLoading">
      <div class="metrics-action-plan__row">
        <div
          class="metrics-action-plan__card metrics-action-plan__card--intervention"
        >
          <div class="metrics-action-plan__card-header">
            <div
              class="metrics-action-plan__card-icon-wrap metrics-action-plan__card-icon-wrap--intervention"
            >
              <eg-icon
                icon="risk-warning-error-danger"
                class="metrics-action-plan__card-icon"
              />
            </div>
            <h4 class="metrics-action-plan__card-title heading-xs">
              {{ $t("metrics.whoNeedsIntervention") }}
            </h4>
          </div>
          <template v-if="intervention">
            <div
              v-if="intervention.students && intervention.students.length"
              class="metrics-action-plan__chips"
            >
              <span
                v-for="(student, i) in intervention.students"
                :key="`${student.id}_${i}`"
                class="metrics-action-plan__chip"
                >{{ student.firstName
                }}{{ student.lastName ? " " + student.lastName : "" }}</span
              >
            </div>
            <p class="metrics-action-plan__card-text paragraph-s">
              {{ intervention.interventionSynthesis }}
            </p>
          </template>
          <div v-else class="metrics-action-plan__no-data paragraph-m">
            {{ $t("metrics.interventionNoData") }}
          </div>
        </div>

        <div
          class="metrics-action-plan__card metrics-action-plan__card--extension"
        >
          <div class="metrics-action-plan__card-header">
            <div
              class="metrics-action-plan__card-icon-wrap metrics-action-plan__card-icon-wrap--extension"
            >
              <eg-icon icon="star" class="metrics-action-plan__card-icon" />
            </div>
            <h4 class="metrics-action-plan__card-title heading-xs">
              {{ $t("metrics.whoIsReadyForExtension") }}
            </h4>
          </div>
          <template v-if="extension">
            <div
              v-if="extension.students && extension.students.length"
              class="metrics-action-plan__chips"
            >
              <span
                v-for="(student, i) in extension.students"
                :key="`${student.id}_${i}`"
                class="metrics-action-plan__chip"
                >{{ student.firstName
                }}{{ student.lastName ? " " + student.lastName : "" }}</span
              >
            </div>
            <p class="metrics-action-plan__card-text paragraph-s">
              {{ extension.extensionSynthesis }}
            </p>
          </template>
          <div v-else class="metrics-action-plan__no-data paragraph-m">
            {{ $t("metrics.extensionNoData") }}
          </div>
        </div>
      </div>

      <div v-if="reteachPriority" class="metrics-action-plan__reteach">
        <div class="metrics-action-plan__reteach-header">
          <div class="metrics-action-plan__reteach-badge">1</div>
          <p class="metrics-action-plan__reteach-label caption-s">
            {{ $t("metrics.reteachPriority") }}
          </p>
        </div>
        <h4 class="metrics-action-plan__reteach-title heading-xs">
          {{ reteachPriority.title }}
        </h4>
        <p class="metrics-action-plan__reteach-text paragraph-s">
          {{ reteachPriority.description }}
        </p>
        <div
          v-if="reteachPriority.alsoWatchOutFor"
          class="metrics-action-plan__reteach-watch"
        >
          <div class="metrics-action-plan__reteach-watch-label">
            <eg-icon
              icon="bulb-light"
              class="metrics-action-plan__reteach-watch-icon"
            />
            <span class="caption-s" style="margin-top: 2px">{{
              $t("metrics.alsoWatchOutFor")
            }}</span>
          </div>
          <p class="metrics-action-plan__reteach-text paragraph-s">
            {{ reteachPriority.alsoWatchOutFor }}
          </p>
        </div>
      </div>

      <div v-if="celebratePublicly" class="metrics-action-plan__celebrate">
        <div class="metrics-action-plan__celebrate-header">
          <div class="metrics-action-plan__celebrate-icon-wrap">
            <eg-icon
              icon="star-filled"
              class="metrics-action-plan__celebrate-icon"
            />
          </div>
          <p class="metrics-action-plan__celebrate-label caption-s">
            {{ $t("metrics.celebratePublicly") }}
          </p>
        </div>
        <h4 class="metrics-action-plan__celebrate-title heading-xs">
          {{ $t("metrics.whatToShareWithClass") }}
        </h4>
        <p class="metrics-action-plan__celebrate-text paragraph-s">
          {{ celebratePublicly }}
        </p>
      </div>
    </template>
  </div>
</template>

<script>
export default {
  name: "MetricsStudentActionPlan",

  props: {
    isLoading: {
      type: Boolean,
      default: false,
    },
    intervention: {
      type: Object,
      default: null,
    },
    extension: {
      type: Object,
      default: null,
    },
    reteachPriority: {
      type: Object,
      default: null,
    },
    celebratePublicly: {
      type: String,
      default: null,
    },
  },
};
</script>

<style lang="scss" scoped>
@import "../shared/metrics_card";

.metrics-action-plan {
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin: 0 24px;

  &__row {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 16px;

    @media (max-width: 900px) {
      grid-template-columns: 1fr;
    }
  }

  &__card {
    @include metrics-card;
    display: flex;
    flex-direction: column;
    gap: 12px;
  }

  &__card-header {
    display: flex;
    align-items: center;
    gap: 10px;
  }

  &__card-icon-wrap {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    border-radius: 8px;
    flex-shrink: 0;

    &--intervention {
      background: var(--colors-orange-100, #ffedd6);
      color: var(--color-surface-semantic-warning-default, #f46b02);
    }

    &--extension {
      background: var(--color-surface-primary-container, #f0ebff);
      color: var(--color-text-primary-default, #7c3aed);
    }
  }

  &__card-icon {
    font-size: 14px;
  }

  &__card-title {
    margin: 0;
    color: var(--color-text-neutral-on-white, #1a2330);
  }

  &__chips {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
  }

  &__chip {
    padding: 4px 10px;
    border-radius: 20px;
    background: var(--color-surface-primary-container, #f0ebff);
    color: var(--color-text-neutral-on-white, #1a2330);
    font-size: var(--typography-font-size-fs-200, 12px);
    font-weight: var(--typography-font-weight-fw-500, 500);
  }

  &__card-text {
    margin: 0;
    color: var(--color-text-neutral-secondary, #566370);
    line-height: 1.5;
  }

  &__no-data {
    display: flex;
    align-items: center;
    justify-content: center;
    flex: 1;
    color: var(--color-text-neutral-on-white);
    text-align: center;
  }

  &__reteach {
    @include metrics-card;
    display: flex;
    flex-direction: column;
    gap: 12px;
  }

  &__reteach-header {
    display: flex;
    align-items: center;
    gap: 10px;
  }

  &__reteach-label {
    color: var(--color-text-primary-default, #7c3aed);
    font-weight: var(--typography-font-weight-fw-600, 600);
    text-transform: uppercase;
    letter-spacing: 0.05em;
    margin: 0;
    display: block;
  }

  &__reteach-badge {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 36px;
    height: 36px;
    border-radius: 6px;
    background: var(--color-surface-primary-container, #f0ebff);
    color: var(--color-text-primary-default, #7c3aed);
    font-size: var(--typography-font-size-fs-200, 12px);
    font-weight: var(--typography-font-weight-fw-700, 700);
    flex-shrink: 0;
  }

  &__reteach-title {
    margin: 0;
    color: var(--color-text-neutral-on-white, #1a2330);
  }

  &__reteach-text {
    margin: 0;
    color: var(--color-text-neutral-secondary, #566370);
    line-height: 1.5;
  }

  &__reteach-watch {
    display: flex;
    flex-direction: column;
    gap: 6px;
    padding: 0.75rem;
    border-radius: 8px;
    background: var(--color-surface-semantic-informative-light);
  }

  &__reteach-watch-label {
    display: flex;
    align-items: center;
    gap: 5px;
    color: var(--color-text-semantic-informative, #17a2b8);
    font-size: var(--typography-font-size-fs-200);
    font-weight: var(--typography-font-weight-fw-600, 600);
  }

  &__celebrate {
    @include metrics-card;
    display: flex;
    flex-direction: column;
    gap: 12px;
  }

  &__celebrate-header {
    display: flex;
    align-items: center;
    gap: 10px;
  }

  &__celebrate-icon-wrap {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 36px;
    height: 36px;
    border-radius: 6px;
    background: var(--color-surface-semantic-success-light, #f0faf3);
    color: var(--color-text-semantic-onSuccess, #308c4a);
    flex-shrink: 0;
  }
  &__reteach-badge,
  &__celebrate-icon {
    font-size: var(--typography-font-size-fs-400);
  }

  &__celebrate-label {
    color: var(--color-text-semantic-onSuccess, #308c4a);
    font-weight: var(--typography-font-weight-fw-600, 600);
    text-transform: uppercase;
    letter-spacing: 0.05em;
    margin: 0;
    display: block;
  }

  &__celebrate-title {
    margin: 0;
    color: var(--color-text-neutral-on-white, #1a2330);
  }

  &__celebrate-text {
    margin: 0;
    color: var(--color-text-neutral-secondary, #566370);
    line-height: 1.5;
  }
}
</style>
